import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { retry, timer, catchError, throwError } from 'rxjs';
import { SrToastService } from '../../shared/components/sr-toast/sr-toast.service';

/**
 * Interceptor de retry automatico para cold start de Railway.
 *
 * Cuando el backend esta dormido (cold start), los primeros requests fallan
 * con status 0 (sin conexion), 502, 503 o 504. Este interceptor reintenta
 * automaticamente hasta 3 veces con delay progresivo (2s, 4s, 6s).
 *
 * NO reintenta errores de negocio (400, 401, 403, 404, 409, 422, 500).
 * Muestra UN solo toast si todos los reintentos fallan.
 *
 * Un POST o un PATCH solo se repite con 503: es el servidor que todavía no atiende, y el pedido no llegó a
 * procesarse. Con 0, 502 o 504 pudo haberse hecho —se perdió la respuesta, no el pedido— y repetirlo creaba una
 * segunda cotización, solicitud u orden igual a la primera. Leer (GET) dos veces no cambia nada, así que se reintenta
 * como antes.
 */

const RETRY_STATUS_CODES = [0, 502, 503, 504];
/** Con estos códigos se sabe que el servidor no procesó el pedido: repetir un envío no lo duplica. */
const NO_PROCESADO = [503];
const ENVIOS = ['POST', 'PATCH'];

function sePuedeRepetir(req: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
  if (!RETRY_STATUS_CODES.includes(error.status) || esErrorDeTenant(error)) return false;
  return !ENVIOS.includes(req.method) || NO_PROCESADO.includes(error.status);
}

/**
 * Códigos que el backend usa para decir que el problema no es de conexión sino
 * de a qué cliente pertenece el dominio. Comparten status con el cold start
 * (503) pero reintentarlos no arregla nada: solo demora seis segundos el mismo
 * error, y tapa la pantalla que explica qué pasa.
 */
const ERRORES_DE_TENANT = [
  'tenant_not_found',
  'tenant_suspended',
  'tenant_maintenance',
  'tenant_mismatch',
  'registro_no_disponible',
];

function esErrorDeTenant(error: HttpErrorResponse): boolean {
  return ERRORES_DE_TENANT.includes(error.error?.error);
}
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

export const retryInterceptor: HttpInterceptorFn = (req, next) => {
  // La cola offline reintenta por su cuenta, con espera creciente (RFC §2.7).
  if (req.headers.has('X-Skip-Retry')) {
    return next(req.clone({ headers: req.headers.delete('X-Skip-Retry') }));
  }
  const toast = inject(SrToastService);

  return next(req).pipe(
    retry({
      count: MAX_RETRIES,
      delay: (error: HttpErrorResponse, retryCount: number) => {
        if (!sePuedeRepetir(req, error)) {
          throw error;
        }

        const delayMs = RETRY_DELAY_MS * retryCount;

        return timer(delayMs);
      },
    }),
    catchError((error: HttpErrorResponse) => {
      // Un envío en segundo plano (la cola offline, #864) avisa por su propio
      // indicador: sin señal, un toast fijo por cada reintento tapaba la
      // pantalla en la que el técnico sigue trabajando.
      if (RETRY_STATUS_CODES.includes(error.status) && !esErrorDeTenant(error)
          && !req.headers.has('X-Skip-Error-Toast')) {
        // Error de conexión: sticky (error() de sr-toast no auto-cierra).
        toast.error(
          'Error de conexión',
          'No se pudo conectar con el servidor. Verifica tu conexión a internet o intenta nuevamente en unos segundos.',
        );
      }
      return throwError(() => error);
    })
  );
};
